<template>
    <div :class="[cssClass, 'autosuggest-input', loading ? 'loading' : '', filteredSuggestions.length > 0 ? 'active' : '', $te(`fields.${inputKey}.description`) ? 'has-description' : '']">
        <div class="input-wrap">
            <CustomInput
                :modelValue="searchText"
                :inputKey="inputKey"
                @update:modelValue="onTextChanged"
                @focus="onFocus"
                :showLabel="showLabel"
                @keydown.down.prevent="highlightNext"
                @keydown.up.prevent="highlightPrevious"
                @keydown.enter.prevent="selectHighlighted"
                ref="suggestInput"
                :showDescription="showDescription"
            />

            <div v-if="loading" class="loading-spinner-type-1 primary"></div>
            <button v-if="allowCommaSeparated" @click.stop="selectCustom()" :disabled="searchText === ''" class="btn-1 btn-s-icon">
                <div class="icon-plus-neg"></div>
            </button>

            <button v-else @click.stop="clearSearch()" :disabled="searchText === ''" class="btn-6 btn-s-icon">
                <div class="icon-reset-neg"></div>
            </button>
        </div>

        <div class="autosuggest-options" v-if="filteredSuggestions.length > 0" ref="suggestionsList">
            <ul @click.stop>
                <li 
                    v-for="(suggestion, index) in filteredSuggestions" 
                    :key="suggestion.value" 
                    :class="{ 'highlighted': index === highlightedIndex, 'has-icon': suggestion.icon, 'has-domain': suggestion.domain }"
                    @click.stop="selectSuggestion(suggestion)"
                >
                    <img v-if="suggestion.icon" :src="suggestion.icon" class="suggestion-icon" alt="" />
                    <div class="suggestion-content">
                        <div class="name">{{ suggestion.text }}</div>
                        <div v-if="suggestion.domain" class="domain">{{ suggestion.domain }}</div>
                    </div>
                </li>
            </ul> 
        </div>
    </div>
</template>

<script>
    import { debounce } from 'lodash';
    import CustomInput from '@/components/elements/inputs/CustomInput';

    export default {
        name: "CustomSuggest",
        components: {
            CustomInput
        },
        props: {
            inputKey: {
                type: String,
                required: true
            },
            fetchSuggestionsFn: {
                type: Function,
                required: true
            },
            excludedValues: {
                type: Array,
                default: () => []
            },
            showLabel: {
                type: Boolean,
                default: false
            },
            showDescription: {
                type: Boolean,
                default: false
            },
            cssClass: {
                type: String,
                default: 'autosuggest-type-4'
            },
            allowCommaSeparated: {
                type: Boolean,
                default: false
            },
            validationType: {
                type: String,
                default: 'none',
                validator: value => ['domain', 'none'].includes(value)
            },
            selectMultiple: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                searchText: '',
                loading: false,
                suggestions: [],
                highlightedIndex: -1
            };
        },
        computed: {
            filteredSuggestions() {
                return this.suggestions.filter(suggestion => 
                    !this.excludedValues.includes(suggestion.value)
                );
            }
        },
        methods: {
            onTextChanged: debounce(function(newValue) {
                if (this.allowCommaSeparated && newValue.includes(',')) {
                    const parts = newValue.split(',');
                    const lastPart = parts.pop().trim();
                    const domains = parts
                        .map(domain => domain.trim())
                        .filter(domain => domain.length > 0);
                    
                    if (domains.length > 0) {
                        this.processEntries(domains);
                        this.searchText = lastPart;
                        this.getSuggestions(lastPart);
                        return;
                    }
                }
                
                this.searchText = newValue;
                this.getSuggestions(newValue);
            }, 300),
            validateEntry(entry) {
                if (this.validationType === 'domain') {
                    // eslint-disable-next-line no-useless-escape
                    const domainRegex = /^[a-zA-Z0-9]+([\-\.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
                    return domainRegex.test(entry);
                }
                return true;
            },
            async processEntries(entries) {
                for (const entry of entries) {
                    if (this.validateEntry(entry) && !this.excludedValues.includes(entry)) {
                        const suggestion = {
                                value: entry,
                                text: entry
                            };

                        this.$emit('select-suggestion', suggestion);
                    }
                }
            },
            async getSuggestions(query) {
                if(this.loading || query.length < 1) {
                    this.suggestions = [];
                    return;
                }

                this.loading = true;

                try {
                    this.suggestions = await this.fetchSuggestionsFn(query);
                } catch {
                    this.suggestions = [];
                } finally {
                    this.highlightedIndex = -1;
                    this.loading = false;
                }
            },
            selectSuggestion(suggestion) {
                if (suggestion instanceof Event || !suggestion || typeof suggestion !== 'object' || this.excludedValues.includes(suggestion.value)) {
                    return;
                }
                
                this.$emit('select-suggestion', suggestion);

                if(!this.selectMultiple) {
                    this.searchText = '';
                    this.suggestions = [];
                }
            },
            clearSearch() {
                this.searchText = '';
                this.suggestions = [];
            },
            onFocus() {
                this.onTextChanged(this.searchText);
            },
            highlightNext() {
                if (this.highlightedIndex < this.filteredSuggestions.length - 1) {
                    this.highlightedIndex++;
                }
            },
            highlightPrevious() {
                if (this.highlightedIndex > 0) {
                    this.highlightedIndex--;
                }
            },
            selectHighlighted() {
                if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredSuggestions.length) {
                    this.selectSuggestion(this.filteredSuggestions[this.highlightedIndex]);
                } else if (this.allowCommaSeparated) {
                    setTimeout(() => {
                        if(this.searchText.trim()) {
                            this.selectCustom();
                        }
                    }, 300); // Offset debounce
                }
            },
            selectCustom() {
                if (this.allowCommaSeparated && this.searchText.trim()) {
                    const currentText = this.searchText.trim();
                    
                    if (this.validateEntry(currentText) && !this.excludedValues.includes(currentText)) {
                        const suggestion = {
                            value: currentText,
                            text: currentText
                        };

                        this.$emit('select-suggestion', suggestion);
                        this.searchText = '';
                        this.suggestions = [];
                    } else if (this.excludedValues.includes(currentText)) {
                        this.searchText = '';
                    }
                }
            },
            handleClickOutside(event) {
                if (this.$refs.suggestionsList && !this.$refs.suggestionsList.contains(event.target)) {
                    this.suggestions = [];
                    this.highlightedIndex = -1;
                }
            }
        },
        mounted() {
            document.addEventListener('click', this.handleClickOutside);
        },
        unmounted() {
            document.removeEventListener('click', this.handleClickOutside);
        }
    }
</script>